import React from 'react';
    import { motion } from 'framer-motion';
    import { useBrandKit } from '../../contexts/BrandKitContext';
    import { assetsApi } from '../../lib/assetsApi';
    import SafeIcon from '../../common/SafeIcon';
    import AssetSelector from './AssetSelector';
    import * as FiIcons from 'react-icons/fi';

    const { FiEdit2, FiTrash2, FiCheck, FiLock, FiImage, FiDroplet } = FiIcons;

    const LOCKED_COLORS = {
      primary: '#03245E',
      accent: '#E2C164',
      background: '#F3F7FA',
      secondary: '#D7B740',
      highlight: '#F7EFE2'
    };

    export default function BrandKitCard({ brandKit, onEdit, onDelete }) {
      const { currentBrandKit, setCurrentBrandKit, updateBrandKit } = useBrandKit();
      const [logoUrl, setLogoUrl] = React.useState(null);
      const [watermarkUrl, setWatermarkUrl] = React.useState(null);
      const [loadingAssets, setLoadingAssets] = React.useState(false);
      const [showAssetSelector, setShowAssetSelector] = React.useState(false);
      const [selectedLogo, setSelectedLogo] = React.useState(null);
      const [selectedWatermark, setSelectedWatermark] = React.useState(null);
      const [updating, setUpdating] = React.useState(false);
      const isSelected = currentBrandKit?.id === brandKit.id;

      const handleSelect = () => {
        setCurrentBrandKit(brandKit);
      };

      React.useEffect(() => {
        const loadAssetThumbnails = async () => {
          setLoadingAssets(true);
          try {
            const logos = brandKit.logos || [];
            const watermarks = brandKit.watermarks || [];

            const logoToLoad = selectedLogo || (logos.find(l => l.id === brandKit.selected_logo_id)) || logos[0];
            if (logoToLoad?.dropbox_path) {
              const url = await assetsApi.getAssetUrl(logoToLoad.dropbox_path);
              setLogoUrl(url);
            } else {
              setLogoUrl(null);
            }

            const watermarkToLoad = selectedWatermark || (watermarks.find(w => w.id === brandKit.selected_watermark_id)) || watermarks[0];
            if (watermarkToLoad?.dropbox_path) {
              const url = await assetsApi.getAssetUrl(watermarkToLoad.dropbox_path);
              setWatermarkUrl(url);
            } else {
              setWatermarkUrl(null);
            }
          } catch (error) {
            console.warn('Failed to load asset thumbnails:', error);
          } finally {
            setLoadingAssets(false);
          }
        };
        loadAssetThumbnails();
      }, [brandKit, selectedLogo, selectedWatermark]);

      React.useEffect(() => {
        const logos = brandKit.logos || [];
        const watermarks = brandKit.watermarks || [];
        if (brandKit.selected_logo_id) {
          const logo = logos.find(l => l.id === brandKit.selected_logo_id);
          if (logo) setSelectedLogo(logo);
        }
        if (brandKit.selected_watermark_id) {
          const watermark = watermarks.find(w => w.id === brandKit.selected_watermark_id);
          if (watermark) setSelectedWatermark(watermark);
        }
      }, [brandKit]);

      const handleSaveSelections = async () => {
        if (!selectedLogo && !selectedWatermark) {
          setShowAssetSelector(false);
          return;
        }
        setUpdating(true);
        try {
          const updates = { ...brandKit, selected_logo_id: selectedLogo?.id || null, selected_watermark_id: selectedWatermark?.id || null };
          await updateBrandKit(brandKit.id, updates);
          setShowAssetSelector(false);
        } catch (error) {
          console.error('Failed to update asset selections:', error);
        } finally {
          setUpdating(false);
        }
      };

      const logos = brandKit.logos || [];
      const watermarks = brandKit.watermarks || [];

      return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }} className={`bg-white rounded-lg shadow-md border-2 transition-all cursor-pointer ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`} onClick={handleSelect} >
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2"> {brandKit.name} </h3>
                {isSelected && (
                  <div className="flex items-center text-blue-600 text-sm"> <SafeIcon icon={FiCheck} className="w-4 h-4 mr-1" /> Active Brand Kit </div>
                )}
              </div>
              <div className="flex space-x-1">
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={(e) => { e.stopPropagation(); onEdit(brandKit); }} className="p-2 text-gray-400 hover:text-blue-600 transition-colors" > <SafeIcon icon={FiEdit2} className="w-4 h-4" /> </motion.button>
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={(e) => { e.stopPropagation(); onDelete(brandKit.id); }} className="p-2 text-gray-400 hover:text-red-600 transition-colors" > <SafeIcon icon={FiTrash2} className="w-4 h-4" /> </motion.button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="relative">
                {loadingAssets ? (
                  <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center"> <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div> </div>
                ) : logoUrl ? (
                  <img src={logoUrl} alt={`${brandKit.name} logo`} className="w-full h-32 object-contain bg-gray-50 rounded-lg border border-gray-200" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                ) : logos.length > 0 ? (
                  <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center"> <SafeIcon icon={FiImage} className="w-10 h-10 text-gray-400" /> </div>
                ) : (
                  <div className="w-full h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center"> <SafeIcon icon={FiImage} className="w-10 h-10 text-gray-300" /> </div>
                )}
                <div className="absolute top-2 left-2 bg-white px-2 py-1 rounded text-xs font-medium text-gray-600 shadow-sm"> Logo {selectedLogo && '✓'} </div>
              </div>
              <div className="relative">
                {loadingAssets ? (
                  <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center"> <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div> </div>
                ) : watermarkUrl ? (
                  <img src={watermarkUrl} alt={`${brandKit.name} watermark`} className="w-full h-32 object-contain bg-gray-50 rounded-lg border border-gray-200" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                ) : watermarks.length > 0 ? (
                  <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center"> <SafeIcon icon={FiDroplet} className="w-10 h-10 text-gray-400" /> </div>
                ) : (
                  <div className="w-full h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center"> <SafeIcon icon={FiDroplet} className="w-10 h-10 text-gray-300" /> </div>
                )}
                <div className="absolute top-2 left-2 bg-white px-2 py-1 rounded text-xs font-medium text-gray-600 shadow-sm"> Watermark {selectedWatermark && '✓'} </div>
              </div>
            </div>
            {(logos.length > 0 || watermarks.length > 0) && (
              <div className="mb-4">
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={(e) => { e.stopPropagation(); setShowAssetSelector(!showAssetSelector); }} className="w-full py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors" > {showAssetSelector ? 'Hide' : 'Show'} Asset Selection </motion.button>
              </div>
            )}
            {showAssetSelector && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-4 p-4 bg-gray-50 rounded-lg" onClick={(e) => e.stopPropagation()} >
                <div className="space-y-4">
                  {logos.length > 0 && (
                    <AssetSelector assets={logos} type="logo" selectedId={selectedLogo?.id} onSelect={setSelectedLogo} title="Select Primary Logo" />
                  )}
                  {watermarks.length > 0 && (
                    <AssetSelector assets={watermarks} type="watermark" selectedId={selectedWatermark?.id} onSelect={setSelectedWatermark} title="Select Watermark" />
                  )}
                  <div className="flex space-x-2">
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleSaveSelections} disabled={updating} className="flex-1 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors" > {updating ? 'Saving...' : 'Save Selections'} </motion.button>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setShowAssetSelector(false)} className="flex-1 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" > Cancel </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center"> <SafeIcon icon={FiLock} className="w-3 h-3 mr-1" /> Locked Brand Colors </h4>
                <div className="grid grid-cols-5 gap-1">
                  {Object.entries(LOCKED_COLORS).map(([name, color]) => (
                    <div key={name} className="w-full h-6 rounded border border-gray-200" style={{ backgroundColor: color }} title={`${name}: ${color}`} />
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center"> <SafeIcon icon={FiLock} className="w-3 h-3 mr-1" /> Locked Typography </h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>Titles: Anton</div>
                  <div>Body: Helvetica World</div>
                  <div>Subtitle: Montserrat</div>
                  <div>Heading: League Spartan</div>
                  <div>Highlight: Gistesy</div>
                </div>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <div className="flex items-center"> <SafeIcon icon={FiImage} className="w-4 h-4 mr-1" /> <span>{logos.length} Logos</span> </div>
                <div className="flex items-center"> <SafeIcon icon={FiDroplet} className="w-4 h-4 mr-1" /> <span>{watermarks.length} Watermarks</span> </div>
              </div>
            </div>
          </div>
        </motion.div>
      );
    }